import React from 'react';
import { FaRocket, FaChartLine, FaUserFriends, FaRegCheckCircle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

interface LandingPageProps {
  darkMode: boolean;
}

const LandingPage: React.FC<LandingPageProps> = ({ darkMode }) => {
  const { t } = useTranslation();

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-neutral-900 text-white' : 'bg-white text-gray-900'}`}>
      {/* Hero Section */}
      <div className={`w-full ${darkMode ? 'bg-blue-950' : 'bg-blue-700'}`}>
        <div className="container mx-auto px-4 py-20">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="md:w-1/2 mb-10 md:mb-0">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                {t('landing.title')}: {t('landing.subtitle')}
              </h1>
              <h2 className="text-2xl md:text-3xl italic text-blue-200 mb-6">
                {t('common.motto')}
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                {t('landing.heroDescription')}
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <a
                  href="https://app.mrbrief.ai/register"
                  className={`${darkMode ? 'bg-white text-blue-900 hover:bg-blue-50' : 'bg-white text-blue-700 hover:bg-blue-50'} px-6 py-3 rounded-lg font-medium text-center transition duration-300 shadow-lg hover:shadow-xl`}
                >
                  {t('landing.getStarted')}
                </a>
                <a
                  href="https://app.mrbrief.ai/login"
                  className={`bg-transparent border-2 border-white text-white hover:bg-white ${darkMode ? 'hover:text-blue-900' : 'hover:text-blue-700'} px-6 py-3 rounded-lg font-medium text-center transition duration-300`}
                >
                  {t('auth.login')}
                </a>
              </div>
            </div>
            <div className="md:w-1/2 flex justify-center">
              <img
                src="/dashboard.png"
                alt="Rapor Dashboard"
                className="w-full max-w-md rounded-lg shadow-2xl"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = '/images/fallback-dashboard.png';
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className={`py-16 ${darkMode ? 'bg-neutral-800' : 'bg-gray-100'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className={`text-3xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('landing.features')}
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${darkMode ? 'text-neutral-300' : 'text-gray-600'}`}>
              {t('landing.featuresDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            <FeatureCard
              icon={<FaRocket />}
              title={t('landing.easyReporting')}
              description={t('landing.easyReportingDesc')}
              darkMode={darkMode}
            />
            <FeatureCard
              icon={<FaChartLine />}
              title={t('landing.progressTracking')}
              description={t('landing.progressTrackingDesc')}
              darkMode={darkMode}
            />
            <FeatureCard
              icon={<FaUserFriends />}
              title={t('landing.teamManagement')}
              description={t('landing.teamManagementDesc')}
              darkMode={darkMode}
            />
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className={`py-16 ${darkMode ? 'bg-neutral-900' : 'bg-white'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className={`text-3xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('landing.howItWorks')}
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${darkMode ? 'text-neutral-300' : 'text-gray-600'}`}>
              {t('landing.howItWorksDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <StepCard
              number="1"
              title={t('landing.createAccount')}
              description={t('landing.createAccountDesc')}
              darkMode={darkMode}
            />
            <StepCard
              number="2"
              title={t('landing.setupTeam')}
              description={t('landing.setupTeamDesc')}
              darkMode={darkMode}
            />
            <StepCard
              number="3"
              title={t('landing.startReporting')}
              description={t('landing.startReportingDesc')}
              darkMode={darkMode}
            />
          </div>
        </div>
      </div>

      {/* Pricing Plans */}
      <div className={`py-16 ${darkMode ? 'bg-neutral-800' : 'bg-gray-100'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className={`text-3xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('landing.pricingPlans')}
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${darkMode ? 'text-neutral-300' : 'text-gray-600'}`}>
              {t('landing.pricingDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <PricingCard
              title="Free"
              price="0$"
              features={[
                "1 team",
                "Up to 5 team members",
                "5 reports limit"
              ]}
              buttonText="Start Free"
              buttonLink="https://app.mrbrief.ai/register"
              darkMode={darkMode}
              highlighted={false}
            />
            <PricingCard
              title="Starter"
              price="4.99$"
              period="monthly"
              features={[
                "2 teams",
                "10 members per team",
                "10 reports limit",
                "4.99$ / monthly",
                "Basic analytics"
              ]}
              buttonText="Sign Up Now"
              buttonLink="https://app.mrbrief.ai/register"
              darkMode={darkMode}
              highlighted={false}
            />
            <PricingCard
              title="Standard"
              price="7.99$"
              period="monthly"
              originalPrice="10$"
              features={[
                "5 teams",
                "50 members per team",
                "50 reports limit",
                "7.99$ / monthly",
                "Advanced analytics"
              ]}
              buttonText="Sign Up Now"
              buttonLink="https://app.mrbrief.ai/register"
              darkMode={darkMode}
              highlighted={true}
              mostPopular={true}
            />
            <PricingCard
              title="Premium"
              price="39.99$"
              period="monthly"
              features={[
                "10 teams",
                "100 members per team",
                "100 reports limit",
                "39.99$ / monthly",
                "Custom integrations",
                "Priority support"
              ]}
              buttonText="Start Premium"
              buttonLink="https://app.mrbrief.ai/register"
              darkMode={darkMode}
              highlighted={false}
              mostPopular={false}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className={`py-10 ${darkMode ? 'bg-neutral-900 text-neutral-300' : 'bg-white text-gray-600'}`}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <h3 className="text-xl font-bold">{t('landing.title')}</h3>
              <p className="mt-2">{t('landing.footerTagline')}</p>
            </div>
            <div className="flex space-x-6">
              <a href="https://app.mrbrief.ai/login" className="hover:underline">{t('auth.login')}</a>
              <a href="https://app.mrbrief.ai/register" className="hover:underline">{t('auth.register')}</a>
              <a href="/policy.html" className="hover:underline">{t('landing.privacyPolicy')}</a>
              <a href="https://app.mrbrief.ai/terms" className="hover:underline">{t('landing.termsOfService')}</a>
            </div>
          </div>
          <div className="mt-8 text-center">
            <p>&copy; {new Date().getFullYear()} {t('landing.title')}. {t('landing.allRightsReserved')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Yardımcı Bileşenler
const FeatureCard: React.FC<{ icon: React.ReactNode; title: string; description: string; darkMode: boolean }> = ({
  icon,
  title,
  description,
  darkMode
}) => {
  return (
    <div className={`p-6 rounded-lg ${darkMode ? 'bg-neutral-700 hover:bg-neutral-600' : 'bg-white hover:bg-gray-50'} shadow-lg transition duration-300 transform hover:-translate-y-1`}>
      <div className={`text-3xl mb-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{icon}</div>
      <h3 className={`text-xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
      <p className={darkMode ? 'text-neutral-300' : 'text-gray-600'}>{description}</p>
    </div>
  );
};

const StepCard: React.FC<{ number: string; title: string; description: string; darkMode: boolean }> = ({
  number,
  title,
  description,
  darkMode
}) => {
  return (
    <div className={`p-6 rounded-lg shadow-lg ${darkMode ? 'bg-neutral-800' : 'bg-white'}`}>
      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 ${darkMode ? 'bg-blue-700' : 'bg-blue-700'}`}>
        {number}
      </div>
      <h3 className={`text-xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
      <p className={darkMode ? 'text-neutral-300' : 'text-gray-600'}>{description}</p>
    </div>
  );
};

const PricingCard: React.FC<{
  title: string;
  price: string;
  period?: string;
  originalPrice?: string;
  features: string[];
  buttonText: string;
  buttonLink: string;
  darkMode: boolean;
  highlighted: boolean;
  mostPopular?: boolean;
}> = ({
  title,
  price,
  period,
  originalPrice,
  features,
  buttonText,
  buttonLink,
  darkMode,
  highlighted,
  mostPopular
}) => {
  return (
    <div className={`p-6 rounded-lg shadow-lg transition duration-300 transform hover:-translate-y-1 relative ${highlighted ? darkMode ? 'bg-blue-900 text-white' : 'bg-blue-700 text-white' : darkMode ? 'bg-neutral-700' : 'bg-white'}`}>
      {mostPopular && (
        <div className="absolute -top-4 left-0 right-0 text-center">
          <span className="bg-yellow-500 text-white px-4 py-1 rounded-full text-sm font-bold">
            Most Popular
          </span>
        </div>
      )}
      <div className="text-center">
        <h3 className={`text-xl font-bold mb-2 ${highlighted ? 'text-white' : darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
        <div className="mb-4">
          {originalPrice && (
            <span className={`text-lg line-through mr-2 ${highlighted ? 'text-blue-200' : darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
              {originalPrice}
            </span>
          )}
          <span className="text-3xl font-bold">{price}</span>
          {period && <span className={`text-sm ${highlighted ? 'text-blue-100' : darkMode ? 'text-neutral-300' : 'text-gray-600'}`}>/{period}</span>}
        </div>
      </div>
      <ul className="mb-6 space-y-2">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center">
            <FaRegCheckCircle className={`mr-2 ${highlighted ? 'text-blue-200' : darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <span className={highlighted ? 'text-white' : darkMode ? 'text-neutral-300' : 'text-gray-600'}>{feature}</span>
          </li>
        ))}
      </ul>
      <div className="text-center">
        <a
          href={buttonLink}
          className={`block px-4 py-2 rounded-lg ${highlighted ? darkMode ? 'bg-white text-blue-900' : 'bg-white text-blue-700' : darkMode ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white'} hover:shadow-lg transition duration-300`}
        >
          {buttonText}
        </a>
      </div>
    </div>
  );
};

export default LandingPage;